import { flushSync } from "react-dom";
import type { RowData } from "@tanstack/react-table";
import { isEqual } from "@common/helpers/comparison";
import type { DataGridListEditBase } from "../../columns/listEditors";
import { type GridController, isFocusLost } from "../../focus/gridController";
import { isSameCellAddress } from "../../focus/navigation";
import type { DataGridCellAddress } from "../../types";
import { getCell, getListEditableCell } from "../cellAccess";
import type { EditContext } from "../editActions";

/**
 * Opens a list cell's editor when this row allows it: the cell becomes the
 * focused cell, so focus returns to it when the editor closes.
 */
export const openListEditor = <TData extends RowData>(
    { instance }: EditContext<TData>,
    address: DataGridCellAddress,
) => {
    if (!getListEditableCell(instance, getCell(instance.table, address))) {
        return false;
    }
    instance.table.setFocusedCell(address.rowId, address.columnId);
    instance.atoms.listCellFocus.set(true);
    instance.atoms.activeEditor.set({ ...address, seed: null });
    return true;
};

const isEditorOpenOn = <TData extends RowData>(
    { instance }: EditContext<TData>,
    address: DataGridCellAddress,
) => {
    const active = instance.atoms.activeEditor.get();
    return active !== null && isSameCellAddress(active, address);
};

/**
 * Closes the cell's editor and returns focus to the cell, unless the close
 * came from a click somewhere else, which keeps its focus. Does nothing once
 * the editor is closed.
 */
export const closeListEditor = <TData extends RowData>(
    context: EditContext<TData>,
    address: DataGridCellAddress,
) => {
    if (!isEditorOpenOn(context, address)) {
        return;
    }
    context.instance.atoms.activeEditor.set(null);
    context.controller.returnFocus(address);
};

/**
 * Whether focus is in the cell, on the cell's own row element (Safari
 * focuses the cell on a button press, and the grid hands that to the row),
 * or outside the grid (a popup), not on another row.
 */
const isFocusClearOfOtherRows = (
    controller: GridController,
    cell: HTMLElement,
) => {
    const active = document.activeElement;
    return (
        cell.contains(active) ||
        active === cell.closest("[data-row-id]") ||
        !controller.root?.contains(active)
    );
};

/**
 * Saves from an editor or a cell control once the event that picked the
 * value is done (a picker refocuses its row after reporting the pick): puts
 * the grid on the cell and focuses it, keeping the quiet state, closes the
 * editor at once, then runs `commit`,
 * so a confirmation it opens returns focus to the cell. Focus that left (a
 * blur) or moved to another row stays put.
 */
export const closeListEditorAndCommit = <TData extends RowData>(
    context: EditContext<TData>,
    { address, commit }: { address: DataGridCellAddress; commit: () => void },
) => {
    const { controller, instance } = context;
    const cell = controller.getElement(address);
    const shouldFocusCell =
        cell !== undefined &&
        !isFocusLost() &&
        isFocusClearOfOtherRows(controller, cell);
    queueMicrotask(() => {
        if (shouldFocusCell && isFocusClearOfOtherRows(controller, cell)) {
            instance.table.setFocusedCell(address.rowId, address.columnId);
            instance.atoms.listCellFocus.set(true);
            cell.focus({ preventScroll: true });
        }
        flushSync(() => closeListEditor(context, address));
        commit();
    });
};

/**
 * Saves a picked value: one `commit` unless it equals the row's current
 * value by content. Editors run it through `closeListEditorAndCommit`, so
 * focus is on the cell before `commit` opens a confirmation. Never awaited.
 */
export const runListCommit = <TData, TValue>({
    edit,
    row,
    value,
}: {
    edit: Pick<DataGridListEditBase<TData, TValue>, "value" | "commit">;
    row: TData;
    value: TValue;
}) => {
    const previousValue = edit.value(row);
    if (isEqual(value, previousValue)) {
        return;
    }
    void edit.commit({ row, value, previousValue });
};

/** An editor's pick handler: closes the editor, focuses the cell, then saves the value. */
export const toCloseAndCommit =
    <TData, TValue>({
        edit,
        row,
        onCloseAndCommit,
    }: {
        edit: Pick<DataGridListEditBase<TData, TValue>, "value" | "commit">;
        row: TData;
        onCloseAndCommit: (commit: () => void) => void;
    }) =>
    (value: TValue) =>
        onCloseAndCommit(() => runListCommit({ edit, row, value }));
